import "./SolibriPlugin.css";
import ContentPage from "./ContentPage";
import Button from "./Button";
import { ReactComponent as DownArrow } from "./static/icons/DownArrow.svg";
import { ReactComponent as RightArrow } from "./static/icons/RightArrow.svg";
import { goToPage } from "./Slide";
import { ReactComponent as UserStories } from "./static/images/UserStories.svg";
import LongCardsCloud from "./LongCardsCloud";
import { ReactComponent as UserFlowPlugin } from "./static/images/UserFlowPlugin.svg";
import Highlighter from "./Highlighter";
import { ReactComponent as Question } from "./static/icons/Question.svg";
import PluginVideo from "./static/videos/PluginVideo.mp4";
import { ReactComponent as Replay } from "./static/icons/Replay.svg";
import { useEffect, useState } from "react";
import { useRef } from "react";
import AnimatedDiv from "./AnimatedDiv";
import { useInView } from "react-intersection-observer";
import { goToElement } from "./LeftNavButton";

function SolibriPlugin() {
  const [videoEnded, setVideoEnded] = useState(false);
  const { ref, inView } = useInView();
  const videoRef = useRef();

  useEffect(() => {
    if (inView && videoRef?.current) {
      videoRef.current.play();
    }
  }, [inView]);

  return (
    <ContentPage>
      <AnimatedDiv>
        <h2>Solibri Inside: Plugin</h2>

        <h3>Role</h3>
        <p>
          Product Design Lead | Product Designer| UX Researcher | UX Writer{" "}
        </p>

        <h3>What's Solibri Inside Plugin?</h3>
        <p>
          Plugin - a product that integrates Solibri Inside functionalities into
          existing modelling tools that architects use, such as Archicad.
        </p>
        <h3>TL;DR</h3>
        <p className="Busy">
          This case is quite long...Busy? Don't worry I got you. Here's the
          summary of the outcome.{" "}
        </p>
        <Button
          className="goToCaseButton"
          variant="complex"
          text="SEE THE FINAL RESULT"
          onClick={() => goToElement("PluginResult")}
          icon={<DownArrow />}
        />
      </AnimatedDiv>
      <AnimatedDiv>
        <h3>Proto persona</h3>
        <p>
          The main proto persona for the Plugin is an architect, who designs
          building models inside of an authoring tool such as Archicad, Bimplus
          or Vectorworks.
        </p>
        <div className="ProtopersonaCard">
          <div className="ArchitectPill">ARCHITECT</div>
          <div className="Heading">
            <div className="name">Attitudes</div>
            <div className="Description">
              Don't care about quality assurance per se; More interested in
              buildings and their project, than the software they are using.
              Quality assurance should be fast otherwise they won't use it. Want
              to avoid being in iteration loop with BIM coordinators. Want to
              focus on my work, while somebody else sets the general project
              settings.
            </div>
          </div>
          <div className="Heading">
            <div className="name">Behaviour</div>
            <div className="Description">
              Not very organised. Get immersed into work and can lose track of
              time, don't like being disturbed while at it. They are creative
              and artistic, and sometimes don't handle criticism well. Care a
              lot about their work and like to stick with the software/tools
              they are familiar with.
            </div>
          </div>
          <div className="Heading">
            <div className="name">Demographic</div>
            <div className="Description">
              Women and men (almost 50/50) between 30 and 50 years old who live
              in big cities in EU, NA and South East Asia.
            </div>
          </div>
          <div className="Heading">
            <div className="name">Pain points</div>
            <div className="Description">
              Local regulations limiting creativity. Can be difficult to stay on
              schedule. Collaboration with other disciplines. Having to rework
              and fix things takes away focus. Moving from 2D to 3D design.
              Complicated tools that are difficult to master. Tools that don't
              help to work more efficiently.
            </div>
          </div>
        </div>
      </AnimatedDiv>
      <AnimatedDiv>
        <h3>Challenges</h3>
        <p>
          1. It can be difficult to stay on schedule because of revisions and
          back-and-forth with BIM Managers.{" "}
        </p>
        <p>
          2. Collaboration with other disciplines doesn't happen in real time,
          since every specialist works in their own design desktop software.
        </p>
        <p>
          3. QA comes after the the design is complete. Reworking and fixing
          things afterwards is very frustrating.{" "}
        </p>
        <p>
          4. Complicated tools that are difficult to master. Architects are
          reluctant to learn to use one more complicated QA software.
        </p>
      </AnimatedDiv>
      <AnimatedDiv>
        <h3>Process</h3>
        <UserStories style={{ margin: "24px 0px" }} />
      </AnimatedDiv>
      <LongCardsCloud
        cardTexts={[
          "I need an enjoyable service experience and modern UI",
          "I need a tool that integrates seamlessly to my authoring software",
          "I want to avoid doing rework",
          "I need to be able to trust the check results",
          "I need a way to customise the checks",
          "I need to identify issues in my project early on",
          "I need an easy introduction to model checking tools",
          "I need to improve my productivity",
        ]}
      ></LongCardsCloud>
      <h3>Visualising the high level user flow</h3>
      <p>
        Here's a high level user flow that clearly demonstrates how easy and
        straightforward the interaction with the Plugin is for the Architect
        user (model author).{" "}
      </p>
      <UserFlowPlugin style={{ margin: "24px 0px" }} />
      <p>
        Designs were tested with users multiple times and iterated more than 10
        times. And finally, we got to the first MVP version.{" "}
      </p>
      <h3 id="PluginResult">Result</h3>
      <Highlighter text="1. Plugin integrates smoothly into a model design tool." />
      <p>
        Web UI available in Archicad, Bimplus and Vectorworks that looks and
        works the same no matter what tool it's integrated in.
      </p>
      <Highlighter text="2. Simple, minimalistic, and modern UI." />
      <p>
        The design has been simplified so that the powerful checking
        capabilities of Solibri inside are just one click away.
      </p>
      <Highlighter text="3. Requirements and specifications based on standards." />
      <p>
        <div
          className="BuildingSmart"
          style={{
            display: "flex",
            alignItems: "baseline",
          }}
        >
          Solibri Inside uses the industry standards and guidelines defined by
          buildingSMART.
          <Button
            variant="icon"
            icon={<Question />}
            onClick={() =>
              window.open("https://www.buildingsmart.org", "_blank")
            }
          ></Button>
        </div>
      </p>
      <Highlighter text="4. Complete real-time sync with Solibri Inside Editor." />
      <p>
        User (for example BIM manager or BIM coordinator) can customise and
        create their own checks based on the project or company needs in Solibri
        Inside Editor. All the checks and changes will be available to Plugin
        users instantly.
      </p>
      <div
        className="VideoAndButton"
        ref={ref}
        style={{ paddingLeft: videoEnded ? "134px" : 0 }}
      >
        <AnimatedDiv>
          <video
            className="PluginVideo"
            src={PluginVideo}
            width="400"
            height="500"
            muted
            ref={videoRef}
            onEnded={() => setVideoEnded(true)}
          ></video>
        </AnimatedDiv>
        {videoEnded && (
          <Button
            className="ReplayButton"
            variant="complex"
            text="REPLAY"
            onClick={() => {
              videoRef?.current.play();
              setVideoEnded(false);
            }}
            icon={<Replay />}
          />
        )}
      </div>
      <Button
        className="goToCaseButton"
        variant="complex"
        text="SEE SOLIBRI INSIDE EDITOR CASE"
        onClick={() => goToPage("/case1/editor")}
        icon={<RightArrow />}
      />
    </ContentPage>
  );
}

export default SolibriPlugin;
